import {
  subcategoriesOf,
  type TaxonomyStatus,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { JobSubcategoryKey } from "@/domains/freelance/taxonomy/types";
import type { JobCategory } from "@/domains/job-offer/create/constants";

/**
 * Pure rules of an offer's subcategories ("ambiti") in the refine dialog and
 * the chat review. No `@/api/client` and no TanStack here, so the node tests
 * can import it.
 */

/**
 * The three states of the subcategories of an offer: not declared (nobody
 * chose, the offer has none), the whole category, or up to
 * `MAX_OFFER_SUBCATEGORIES` chosen keys.
 */
export type OfferSubcategories =
  | { kind: "undeclared" }
  | { kind: "whole" }
  | { kind: "chosen"; keys: JobSubcategoryKey[] };

/** The most subcategories an offer can carry. */
export const MAX_OFFER_SUBCATEGORIES = 3;

/** The taxonomy as the rules read it; the store by default, a fake in tests. */
export type OfferSubcategoryTaxonomy = {
  subcategoriesOf: (category: string) => string[];
  status: () => TaxonomyStatus;
};

const storeTaxonomy: OfferSubcategoryTaxonomy = {
  subcategoriesOf,
  status: taxonomyStatus,
};

/**
 * The state a proposal from the AI opens the review in: the keys in the order
 * received, or `undeclared` when it sends none. The AI never proposes "whole".
 */
export function fromAiProposal(
  keys: readonly JobSubcategoryKey[] | null | undefined,
): OfferSubcategories {
  if (!keys || keys.length === 0) return { kind: "undeclared" };
  return { kind: "chosen", keys: [...keys] };
}

/**
 * What the review shows, computed at read time and never written back.
 * Without a category there are no keys. While the tree is not ready the value
 * is kept as it is. With the tree, only children of the category stay, in
 * their order; if nothing is left the state is `undeclared`.
 */
export function visibleSubcategories(
  category: JobCategory | null | undefined,
  value: OfferSubcategories,
  taxonomy: OfferSubcategoryTaxonomy = storeTaxonomy,
): OfferSubcategories {
  if (value.kind !== "chosen") return value;
  if (!category) return { kind: "undeclared" };
  if (taxonomy.status() !== "ready") return value;
  const children = taxonomy.subcategoriesOf(category);
  const keys = value.keys.filter((key) => children.includes(key));
  return keys.length > 0 ? { kind: "chosen", keys } : { kind: "undeclared" };
}

/**
 * Adds or removes a subcategory. A chosen key is always removed, even when
 * full; removing the last one gives "whole". A new key is added only if it is
 * a child of the category and there is room; the result keeps the order of the
 * tree. Otherwise the same value comes back.
 */
export function toggleOfferSubcategory(
  category: JobCategory | null,
  value: OfferSubcategories,
  key: JobSubcategoryKey,
  taxonomy: OfferSubcategoryTaxonomy = storeTaxonomy,
): OfferSubcategories {
  if (value.kind === "chosen" && value.keys.includes(key)) {
    const keys = value.keys.filter((k) => k !== key);
    return keys.length > 0 ? { kind: "chosen", keys } : { kind: "whole" };
  }
  if (category === null) return value;
  const children = taxonomy.subcategoriesOf(category);
  if (!children.includes(key)) return value;
  if (isFull(value)) return value;
  const current = value.kind === "chosen" ? value.keys : [];
  const keys = [...current, key].sort(
    (a, b) => children.indexOf(a) - children.indexOf(b),
  );
  return { kind: "chosen", keys };
}

/** "Tutta la categoria": the whole category, whatever was chosen. */
export function chooseWholeCategory(): OfferSubcategories {
  return { kind: "whole" };
}

/** Whether no other subcategory can be added. */
export function isFull(value: OfferSubcategories): boolean {
  return (
    value.kind === "chosen" && value.keys.length >= MAX_OFFER_SUBCATEGORIES
  );
}

/**
 * The `job_subcategory` field of the body: chosen keys in order, `[]` for the
 * whole category, and `undefined` (field absent) when not declared.
 */
export function toJobSubcategoryField(
  value: OfferSubcategories,
): JobSubcategoryKey[] | undefined {
  if (value.kind === "chosen") return [...value.keys];
  if (value.kind === "whole") return [];
  return undefined;
}

/**
 * The `job_subcategory` of the final preview: what the review step shows, the
 * same rule as `toJobSubcategoryField` on the visible value. Never written back.
 */
export function previewSubcategoryField(
  category: JobCategory | null | undefined,
  value: OfferSubcategories,
  taxonomy: OfferSubcategoryTaxonomy = storeTaxonomy,
): JobSubcategoryKey[] | undefined {
  return toJobSubcategoryField(visibleSubcategories(category, value, taxonomy));
}

/**
 * The category picked in the dialog. `valueCategory` is the category the
 * subcategories belong to; the comparison is against it and not against the
 * previous value of the Select. Kobalte `CategorySelect` never calls
 * `onChange` with the category already chosen: picking it again clears it
 * (`onChange(null)`). So after X, then null, then X the previous value of the
 * Select is null, and comparing against it would wrongly read a return to X as
 * a change of category and drop the subcategories.
 *
 * - `next` null: nothing changes, `notice` null (an earlier notice stays);
 * - `next` equal to `valueCategory`: the value stays, `notice` null;
 * - any other category: "whole", and `notice` true if there was a category
 *   before, so the dialog can say the subcategories were removed.
 */
export function changeOfferCategory({
  valueCategory,
  current,
  next,
}: {
  valueCategory: JobCategory | null;
  current: OfferSubcategories;
  next: JobCategory | null;
}): {
  value: OfferSubcategories;
  valueCategory: JobCategory | null;
  notice: boolean | null;
} {
  if (next === null || next === valueCategory) {
    return { value: current, valueCategory, notice: null };
  }
  return {
    value: chooseWholeCategory(),
    valueCategory: next,
    notice: valueCategory !== null,
  };
}
